import type {
  WhatsAppStatus,
  WhatsAppGroup,
  WhatsAppBinding,
  WhatsAppMessage,
  WhatsAppMessageLink,
  WhatsAppOutbox,
  WhatsAppBeaconProposal,
  WhatsAppReviewGroups,
} from './whatsapp-types'
import type {
  ClassifyRequest,
  ClassifyResponse,
  TaxEstimateRequest,
  TaxEstimateResponse,
  ParsedInvoice,
  ParsedInquirySource,
  ApiError,
  ApiResponse,
  Customer,
  Consignment,
  ConsignmentDetail,
  CreateConsignmentRequest,
  CreateConsignmentResponse,
  CreateCommunicationRequest,
  CreateInquiryRequest,
  CreateInquiryTaskRequest,
  InquiryCustomerAssociationPreview,
  ApplyInquiryCustomerAssociationRequest,
  ConvertInquiryRequest,
  UpdateConsignmentItemRequest,
  UpdateConsignmentRequest,
  UpdateInquiryRequest,
  UpdateInquiryTaskRequest,
  BatchInquiryItem,
  BatchResponse,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CustomerSignInEmailStatus,
  SignInEmailPreview,
  SignInEmailStatus,
  HelmOverview,
  HelmNotification,
  HelmNotificationList,
  FeedbackReportInput,
  FeedbackReportResult,
  HelmAttention,
  HelmDisplaySnapshot,
  HelmAttachmentAccess,
  HelmInquiryDocumentAccess,
  HelmJobStatus,
  HelmReplyDraft,
  HelmNotificationPreferences,
  HelmOnboardingProgress,
  HelmOnboardingProgressUpdate,
  Profile,
  HelmStaffInvitation,
  HelmInvitationPreview,
  HelmUser,
  HelmUsersResponse,
  HelmCommunication,
  HelmOutboundCommunicationAttachment,
  Inquiry,
  ListCommunicationsRequest,
  WorkflowTask,
  AttachConsignmentDocumentRequest,
  ConsignmentDocument,
  ConsignmentDocumentType,
  GenerateConsignmentDocumentRequest,
  ReplaceConsignmentDocumentRequest,
  ConsignmentItem,
  ConsignmentTrackingEvent,
  CreateTrackingEventRequest,
  ConsignmentSeaContainer,
  ConsignmentSeaShipment,
  CreateSeaContainerRequest,
  CreateSeaShipmentRequest,
  SeaTrackingContainersResponse,
  SeaTrackingImportRequest,
  SeaTrackingImportResponse,
  SeaTrackingShipment,
  SeaVesselCurrentPosition,
  RecalculateConsignmentTaxResponse,
  StorageUploadInitRequest,
  StorageUploadInitResponse,
  CreateHelmInvitationRequest,
  ConsignmentGathering,
  ParcelAddressInfo,
  PreAlertParcelRequest,
  ProcessParcelRequest,
  UpdateMyParcelRequest,
  WarehouseParcel,
  WarehouseRateCard,
  WarehouseView,
  UpdateHelmUserRolesRequest,
  UpdateSeaContainerRequest,
  UpdateSeaShipmentRequest,
  ApplyBillOfLadingProposalRequest,
  BillOfLadingReviewProposal,
  CreateBillOfLadingProposalRequest,
  MaritimeCatalogs,
  MaritimeContainerReconciliation,
  MaritimeReconciliationDecision,
  CreateTrackingShareRequest,
  PublicTrackingDocument,
  PublicTrackingExchangeResponse,
  PublicTrackingContainer,
  PublicTrackingPosition,
  PublicTrackingSummary,
  PublicTrackingTimelineEvent,
  TrackingShare,
  TrackingShareReveal,
  UpdateTrackingShareRequest,
} from './types'
import { reportCustomerSuspended } from '../../src/lib/account-suspension'
import { API_BASE_URL } from '../env'

/** One page of a list endpoint, with the counts needed to render controls. */
export interface PageOf<T> {
  items: T[]
  page: number
  limit: number
  total: number
  totalPages: number
}

/**
 * The message for a failure the API did not explain. A bare 404 means the API
 * this page talks to has no such endpoint: Helm's pages and its API deploy
 * separately, so a new page can be live before the API that serves it.
 */
function fallbackMessage(response: Response): string {
  if (response.status === 404) {
    return 'The Helm API here does not have this yet. It will work once the latest API is deployed.'
  }
  return `HTTP ${response.status}${response.statusText ? `: ${response.statusText}` : ''}`
}

export class ApiClientError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: unknown

  constructor(message: string, status: number, code = 'API_ERROR', details?: unknown) {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    // Any refusal for suspension, from any request, locks the whole shell.
    if (code === 'CUSTOMER_SUSPENDED') reportCustomerSuspended()
    this.code = code
    this.details = details
  }
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const headers = new Headers(options.headers)
    if (!(options.body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }

    const config: RequestInit = {
      ...options,
      credentials: 'include',
      headers,
    }

    try {
      const response = await fetch(url, config)

      if (!response.ok) {
        let error: ApiError | null = null
        try {
          error = (await response.json()) as ApiError
        } catch {
          // Keep non-JSON failures on the sanitized HTTP fallback.
        }
        throw new ApiClientError(
          error?.error?.message || fallbackMessage(response),
          response.status,
          error?.error?.code,
          error?.error?.details,
        )
      }

      const json: ApiResponse<T> = await response.json()
      return json.data
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('An unknown error occurred')
    }
  }

  /**
   * Fetches one page of a list endpoint.
   *
   * `request` returns `json.data` and drops the pagination block, which is
   * correct everywhere else and useless here. An endpoint not yet paginated
   * answers without the block, so this reports a single page covering whatever
   * it returned rather than pretending there is more.
   */
  private async requestPage<T>(
    endpoint: string,
    params: Record<string, string>,
  ): Promise<PageOf<T>> {
    const search = new URLSearchParams(params)
    const response = await fetch(`${this.baseUrl}${endpoint}?${search}`, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
    const body = await response.json()
    if (!response.ok) {
      throw new ApiClientError(
        body?.error?.message || 'Request failed',
        response.status,
        body?.error?.code,
      )
    }

    const items = (body.data ?? []) as T[]
    return {
      items,
      page: body.pagination?.page ?? 1,
      limit: body.pagination?.limit ?? items.length,
      total: body.pagination?.total ?? items.length,
      totalPages: body.pagination?.totalPages ?? 1,
    }
  }

  async listInquiriesPage(
    page: number,
    limit = 25,
    filters: {
      owner?: 'me' | 'unassigned' | string
      open?: boolean
      /** Cancelled inquiries: with the rest (the default), left out, or alone. */
      cancelled?: 'include' | 'exclude' | 'only'
    } = {},
  ): Promise<PageOf<Inquiry>> {
    return this.requestPage<Inquiry>('/inquiries', {
      page: String(page),
      limit: String(limit),
      ...(filters.owner ? { owner: filters.owner } : {}),
      ...(filters.open ? { open: 'true' } : {}),
      ...(filters.cancelled ? { cancelled: filters.cancelled } : {}),
    })
  }

  async listCustomersPage(
    page: number,
    options: { limit?: number; includeArchived?: boolean; search?: string } = {},
  ): Promise<PageOf<Customer>> {
    const params: Record<string, string> = {
      page: String(page),
      limit: String(options.limit ?? 25),
    }
    if (options.includeArchived) params.include_archived = 'true'
    if (options.search) params.search = options.search
    return this.requestPage<Customer>('/customers', params)
  }

  /**
   * Translates texts the page shows, in order. Only these texts are sent and
   * translated; text already in `target` comes back as it is.
   */
  async translate(
    texts: string[],
    target: 'en' | 'zh' | 'fr',
    context?: string,
  ): Promise<{
    translations: Array<{ text: string; source_language: string | null; translated: boolean }>
  }> {
    return this.request('/translate', {
      method: 'POST',
      body: JSON.stringify(context ? { texts, target, context } : { texts, target }),
    })
  }

  /**
   * Classify an item to get HS code using AI
   */
  async classify(data: ClassifyRequest): Promise<ClassifyResponse> {
    return this.request<ClassifyResponse>('/classify', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Estimate taxes for a consignment
   */
  async estimateTax(data: TaxEstimateRequest): Promise<TaxEstimateResponse> {
    return this.request<TaxEstimateResponse>('/tax/estimate', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Parse invoice to extract items
   */
  async parseInvoice(imageUrl: string): Promise<ParsedInvoice> {
    return this.request<ParsedInvoice>('/parse-invoice', {
      method: 'POST',
      body: JSON.stringify({ image_url: imageUrl }),
    })
  }

  async parseInquirySource(
    sourceUrl: string,
    sourceChannel?: string | null,
  ): Promise<ParsedInquirySource> {
    return this.request<ParsedInquirySource>('/inquiries/parse-source', {
      method: 'POST',
      body: JSON.stringify({ source_url: sourceUrl, source_channel: sourceChannel ?? null }),
    })
  }

  /**
   * Create a new consignment
   */
  async createConsignment(data: CreateConsignmentRequest): Promise<CreateConsignmentResponse> {
    return this.request<CreateConsignmentResponse>('/consignments', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async listConsignments(): Promise<Consignment[]> {
    return this.request<Consignment[]>('/consignments')
  }

  async getConsignment(id: string): Promise<ConsignmentDetail> {
    return this.request<ConsignmentDetail>(`/consignments/${id}`)
  }

  async updateConsignment(id: string, data: UpdateConsignmentRequest): Promise<Consignment> {
    return this.request<Consignment>(`/consignments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteConsignment(id: string): Promise<{ deleted: true }> {
    return this.request<{ deleted: true }>(`/consignments/${id}`, {
      method: 'DELETE',
    })
  }

  async updateConsignmentItem(
    consignmentId: string,
    itemId: string,
    data: UpdateConsignmentItemRequest,
  ): Promise<ConsignmentItem> {
    return this.request<ConsignmentItem>(`/consignments/${consignmentId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async recalculateConsignmentTaxes(
    consignmentId: string,
    data: { currency?: string; target_currency?: string } = {},
  ): Promise<RecalculateConsignmentTaxResponse> {
    return this.request<RecalculateConsignmentTaxResponse>(
      `/consignments/${consignmentId}/tax-estimates/recalculate`,
      {
        method: 'POST',
        body: JSON.stringify({
          currency: data.currency ?? 'USD',
          target_currency: data.target_currency ?? 'UGX',
        }),
      },
    )
  }

  async generateConsignmentDocument(
    consignmentId: string,
    type: ConsignmentDocumentType,
    data: GenerateConsignmentDocumentRequest = {},
  ): Promise<ConsignmentDocument> {
    return this.request<ConsignmentDocument>(`/consignments/${consignmentId}/documents/generate`, {
      method: 'POST',
      body: JSON.stringify({ type, ...data }),
    })
  }

  async attachConsignmentDocument(
    consignmentId: string,
    data: AttachConsignmentDocumentRequest,
  ): Promise<ConsignmentDocument> {
    return this.request<ConsignmentDocument>(`/consignments/${consignmentId}/documents`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async replaceConsignmentDocument(
    id: string,
    data: ReplaceConsignmentDocumentRequest,
  ): Promise<ConsignmentDocument> {
    return this.request<ConsignmentDocument>(`/documents/${id}/replace`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async deleteConsignmentDocument(id: string): Promise<{ deleted: true }> {
    return this.request<{ deleted: true }>(`/documents/${id}`, {
      method: 'DELETE',
    })
  }

  getDocumentDownloadUrl(id: string): string {
    return `${this.baseUrl}/documents/${id}/download`
  }

  async createStorageUpload(data: StorageUploadInitRequest): Promise<StorageUploadInitResponse> {
    return this.request<StorageUploadInitResponse>('/storage/uploads', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async uploadToSignedUrl(upload: StorageUploadInitResponse, file: File): Promise<void> {
    const response = await fetch(upload.upload_url, {
      method: upload.upload_method,
      headers: upload.upload_headers,
      body: file,
    })

    if (!response.ok) {
      throw new Error(`Upload failed with HTTP ${response.status}`)
    }
  }

  async recordConsignmentTrackingEvent(
    consignmentId: string,
    data: CreateTrackingEventRequest,
  ): Promise<ConsignmentTrackingEvent> {
    return this.request<ConsignmentTrackingEvent>(
      `/consignments/${consignmentId}/tracking-events`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )
  }

  async createConsignmentSeaShipment(
    consignmentId: string,
    data: CreateSeaShipmentRequest,
  ): Promise<ConsignmentSeaShipment> {
    return this.request<ConsignmentSeaShipment>(`/consignments/${consignmentId}/sea-shipments`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateConsignmentSeaShipment(
    consignmentId: string,
    shipmentId: string,
    data: UpdateSeaShipmentRequest,
  ): Promise<ConsignmentSeaShipment> {
    return this.request<ConsignmentSeaShipment>(
      `/consignments/${consignmentId}/sea-shipments/${shipmentId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    )
  }

  async deleteConsignmentSeaShipment(
    consignmentId: string,
    shipmentId: string,
  ): Promise<{ deleted: true }> {
    return this.request<{ deleted: true }>(
      `/consignments/${consignmentId}/sea-shipments/${shipmentId}`,
      {
        method: 'DELETE',
      },
    )
  }

  async createConsignmentSeaContainer(
    consignmentId: string,
    shipmentId: string,
    data: CreateSeaContainerRequest,
  ): Promise<ConsignmentSeaContainer> {
    return this.request<ConsignmentSeaContainer>(
      `/consignments/${consignmentId}/sea-shipments/${shipmentId}/containers`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )
  }

  async updateConsignmentSeaContainer(
    consignmentId: string,
    shipmentId: string,
    containerId: string,
    data: UpdateSeaContainerRequest,
  ): Promise<ConsignmentSeaContainer> {
    return this.request<ConsignmentSeaContainer>(
      `/consignments/${consignmentId}/sea-shipments/${shipmentId}/containers/${containerId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    )
  }

  async deleteConsignmentSeaContainer(
    consignmentId: string,
    shipmentId: string,
    containerId: string,
  ): Promise<{ deleted: true }> {
    return this.request<{ deleted: true }>(
      `/consignments/${consignmentId}/sea-shipments/${shipmentId}/containers/${containerId}`,
      {
        method: 'DELETE',
      },
    )
  }

  async getMaritimeCatalogs(): Promise<MaritimeCatalogs> {
    return this.request<MaritimeCatalogs>('/maritime/catalogs')
  }

  async createBillOfLadingProposal(
    consignmentId: string,
    data: CreateBillOfLadingProposalRequest,
  ): Promise<BillOfLadingReviewProposal> {
    return this.request<BillOfLadingReviewProposal>(
      `/consignments/${consignmentId}/maritime/bl-proposals`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )
  }

  async listBillOfLadingProposals(consignmentId: string): Promise<BillOfLadingReviewProposal[]> {
    return this.request<BillOfLadingReviewProposal[]>(
      `/consignments/${consignmentId}/maritime/bl-proposals`,
    )
  }

  async applyBillOfLadingProposal(
    consignmentId: string,
    proposalId: string,
    data: ApplyBillOfLadingProposalRequest,
  ): Promise<BillOfLadingReviewProposal> {
    return this.request<BillOfLadingReviewProposal>(
      `/consignments/${consignmentId}/maritime/bl-proposals/${proposalId}/apply`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )
  }

  async rejectBillOfLadingProposal(
    consignmentId: string,
    proposalId: string,
  ): Promise<BillOfLadingReviewProposal> {
    return this.request<BillOfLadingReviewProposal>(
      `/consignments/${consignmentId}/maritime/bl-proposals/${proposalId}/reject`,
      { method: 'POST' },
    )
  }

  async listMaritimeReconciliations(
    consignmentId: string,
  ): Promise<MaritimeContainerReconciliation[]> {
    return this.request<MaritimeContainerReconciliation[]>(
      `/consignments/${consignmentId}/maritime/reconciliations`,
    )
  }

  async resolveMaritimeReconciliation(
    consignmentId: string,
    reconciliationId: string,
    data: {
      decision: MaritimeReconciliationDecision
      shipment_id?: string | null
      operational_container_id?: string | null
    },
  ): Promise<MaritimeContainerReconciliation> {
    return this.request<MaritimeContainerReconciliation>(
      `/consignments/${consignmentId}/maritime/reconciliations/${reconciliationId}/resolve`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )
  }

  async importSeaTrackingShipment(
    data: SeaTrackingImportRequest,
  ): Promise<SeaTrackingImportResponse> {
    return this.request<SeaTrackingImportResponse>('/tracking/sea/shipments/imports', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getSeaTrackingShipment(
    reference: string,
    options: { forceRefresh?: boolean } = {},
  ): Promise<SeaTrackingShipment> {
    const query = new URLSearchParams({ reference })
    if (options.forceRefresh) query.set('force_refresh', 'true')
    return this.request<SeaTrackingShipment>(
      `/tracking/sea/shipments/by-reference?${query.toString()}`,
    )
  }

  async listSeaTrackingContainers(
    reference: string,
    page = 1,
    pageSize = 100,
    options: { forceRefresh?: boolean } = {},
  ): Promise<SeaTrackingContainersResponse> {
    const query = new URLSearchParams({
      reference,
      page: String(page),
      page_size: String(pageSize),
    })
    if (options.forceRefresh) query.set('force_refresh', 'true')
    return this.request<SeaTrackingContainersResponse>(
      `/tracking/sea/shipments/by-reference/containers?${query.toString()}`,
    )
  }

  async getSeaVesselCurrentPosition(
    reference: string,
    consignmentId: string,
  ): Promise<SeaVesselCurrentPosition> {
    const query = new URLSearchParams({
      reference,
      consignment_id: consignmentId,
    })
    return this.request<SeaVesselCurrentPosition>(
      `/tracking/sea/vessels/current-position?${query.toString()}`,
    )
  }

  async listCommunications(params: ListCommunicationsRequest): Promise<HelmCommunication[]> {
    const query = new URLSearchParams()
    if (params.customer_id) query.set('customer_id', params.customer_id)
    if (params.consignment_id) query.set('consignment_id', params.consignment_id)
    if (params.inquiry_id) query.set('inquiry_id', params.inquiry_id)
    if (params.limit) query.set('limit', String(params.limit))
    const suffix = query.toString() ? `?${query.toString()}` : ''

    return this.request<HelmCommunication[]>(`/communications${suffix}`)
  }

  async createCommunication(data: CreateCommunicationRequest): Promise<HelmCommunication> {
    const payload =
      data.send && !data.idempotency_key ? { ...data, idempotency_key: crypto.randomUUID() } : data
    return this.request<HelmCommunication>('/communications', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async uploadCommunicationAttachment(file: File): Promise<HelmOutboundCommunicationAttachment> {
    const form = new FormData()
    form.set('file', file)
    return this.request<HelmOutboundCommunicationAttachment>(
      '/communications/attachments/uploads',
      {
        method: 'POST',
        body: form,
      },
    )
  }

  async listWarehouseParcels(view: WarehouseView, q?: string): Promise<WarehouseParcel[]> {
    const params = new URLSearchParams({ view })
    if (q?.trim()) params.set('q', q.trim())
    return this.request<WarehouseParcel[]>(`/warehouse/parcels?${params}`)
  }

  async getWarehouseParcel(parcelId: string): Promise<WarehouseParcel> {
    return this.request<WarehouseParcel>(`/warehouse/parcels/${parcelId}`)
  }

  /** The parcel expected under this tracking number, or a new one for the code on its label. */
  async registerParcelArrival(input: {
    tracking_number?: string | null
    customer_code?: string | null
    description?: string | null
  }): Promise<WarehouseParcel> {
    return this.request<WarehouseParcel>('/warehouse/arrivals', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }

  async claimParcel(parcelId: string, customerCode: string): Promise<WarehouseParcel> {
    return this.request<WarehouseParcel>(`/warehouse/parcels/${parcelId}/claim`, {
      method: 'POST',
      body: JSON.stringify({ customer_code: customerCode }),
    })
  }

  async uploadParcelPhoto(parcelId: string, photo: File): Promise<{ id: string }> {
    const form = new FormData()
    form.set('photo', photo)
    return this.request<{ id: string }>(`/warehouse/parcels/${parcelId}/photos`, {
      method: 'POST',
      body: form,
    })
  }

  /** A private parcel photo as an object URL; revoke it when done. */
  async parcelPhotoUrl(parcelId: string, photoId: string): Promise<string> {
    const response = await fetch(
      `${this.baseUrl}/warehouse/parcels/${parcelId}/photos/${photoId}`,
      { credentials: 'include' },
    )
    if (!response.ok) throw new ApiClientError(fallbackMessage(response), response.status)
    return URL.createObjectURL(await response.blob())
  }

  /** Adds a package's domestic tracking number to a consignment and follows it with the courier. */
  async addConsignmentDomesticShipment(
    consignmentId: string,
    data: { tracking_number: string; supplier_name?: string | null },
  ): Promise<ConsignmentGathering> {
    return this.request<ConsignmentGathering>(`/consignments/${consignmentId}/domestic-shipments`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /** Asks the courier now about a consignment's package, or registers its number again. */
  async refreshConsignmentDomesticShipment(
    consignmentId: string,
    shipmentId: string,
  ): Promise<ConsignmentGathering> {
    return this.request<ConsignmentGathering>(
      `/consignments/${consignmentId}/domestic-shipments/${shipmentId}/tracking/refresh`,
      { method: 'POST' },
    )
  }

  /** Removes a package added by hand that no item travels in. */
  async removeConsignmentDomesticShipment(
    consignmentId: string,
    shipmentId: string,
  ): Promise<ConsignmentGathering> {
    return this.request<ConsignmentGathering>(
      `/consignments/${consignmentId}/domestic-shipments/${shipmentId}`,
      { method: 'DELETE' },
    )
  }

  /** A photo of the parcel the warehouse received for a consignment, as an object URL. */
  async consignmentParcelPhotoUrl(consignmentId: string, photoId: string): Promise<string> {
    const response = await fetch(
      `${this.baseUrl}/consignments/${consignmentId}/domestic-shipments/photos/${photoId}`,
      { credentials: 'include' },
    )
    if (!response.ok) throw new ApiClientError(fallbackMessage(response), response.status)
    return URL.createObjectURL(await response.blob())
  }

  /** Asks the courier's tracking service now, or registers a number whose registration failed. */
  async refreshParcelTracking(
    parcelId: string,
  ): Promise<WarehouseParcel & { refresh: 'REFRESHED' | 'REGISTERING' }> {
    return this.request<WarehouseParcel & { refresh: 'REFRESHED' | 'REGISTERING' }>(
      `/warehouse/parcels/${parcelId}/tracking/refresh`,
      { method: 'POST' },
    )
  }

  async processParcel(parcelId: string, data: ProcessParcelRequest): Promise<WarehouseParcel> {
    return this.request<WarehouseParcel>(`/warehouse/parcels/${parcelId}/process`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getWarehouseRates(): Promise<{ current: WarehouseRateCard; history: WarehouseRateCard[] }> {
    return this.request('/warehouse/rates')
  }

  async addWarehouseRates(data: {
    general_per_kg: number
    dangerous_per_kg: number
    minimum_kg: number
    effective_from?: string | null
    note?: string | null
  }): Promise<WarehouseRateCard> {
    return this.request<WarehouseRateCard>('/warehouse/rates', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /** The signed-in customer's warehouse address (null until issued), rates and rules. */
  async getParcelAddress(): Promise<ParcelAddressInfo> {
    return this.request<ParcelAddressInfo>('/parcels/address')
  }

  /** Issues the customer's code; refused with EMAIL_NOT_CONFIRMED until their email is. */
  async issueParcelAddress(): Promise<ParcelAddressInfo> {
    return this.request<ParcelAddressInfo>('/parcels/address', { method: 'POST' })
  }

  async listMyParcels(): Promise<WarehouseParcel[]> {
    return this.request<WarehouseParcel[]>('/parcels')
  }

  async preAlertParcel(data: PreAlertParcelRequest): Promise<WarehouseParcel> {
    return this.request<WarehouseParcel>('/parcels', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateMyParcel(parcelId: string, data: UpdateMyParcelRequest): Promise<WarehouseParcel> {
    return this.request<WarehouseParcel>(`/parcels/${parcelId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /** A photo of the customer's own parcel as an object URL; revoke it when done. */
  async myParcelPhotoUrl(parcelId: string, photoId: string): Promise<string> {
    const response = await fetch(`${this.baseUrl}/parcels/${parcelId}/photos/${photoId}`, {
      credentials: 'include',
    })
    if (!response.ok) throw new ApiClientError(fallbackMessage(response), response.status)
    return URL.createObjectURL(await response.blob())
  }

  async listInquiries(): Promise<Inquiry[]> {
    return this.request<Inquiry[]>('/inquiries')
  }

  async getInquiry(id: string): Promise<Inquiry> {
    return this.request<Inquiry>(`/inquiries/${id}`)
  }

  async createInquiry(data: CreateInquiryRequest): Promise<Inquiry> {
    return this.request<Inquiry>('/inquiries', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateInquiry(id: string, data: UpdateInquiryRequest): Promise<Inquiry> {
    return this.request<Inquiry>(`/inquiries/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async previewInquiryCustomerAssociation(
    inquiryId: string,
    targetCustomerId: string,
  ): Promise<InquiryCustomerAssociationPreview> {
    const query = new URLSearchParams({ target_customer_id: targetCustomerId })
    return this.request(`/inquiries/${encodeURIComponent(inquiryId)}/customer-association?${query}`)
  }

  async applyInquiryCustomerAssociation(
    inquiryId: string,
    input: ApplyInquiryCustomerAssociationRequest,
  ): Promise<Inquiry> {
    return this.request(`/inquiries/${encodeURIComponent(inquiryId)}/customer-association`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }

  /** Updates or cancels up to 100 inquiries, each as on its own; a result for every one. */
  async batchInquiries(items: BatchInquiryItem[]): Promise<BatchResponse<Inquiry>> {
    return this.request<BatchResponse<Inquiry>>('/inquiries/batch', {
      method: 'POST',
      body: JSON.stringify({ items }),
    })
  }

  /** Updates up to 100 consignments, each as on its own; a result for every one. */
  async batchConsignments(
    items: Array<{ id: string; update: UpdateConsignmentRequest }>,
  ): Promise<BatchResponse<Consignment>> {
    return this.request<BatchResponse<Consignment>>('/consignments/batch', {
      method: 'POST',
      body: JSON.stringify({ items }),
    })
  }

  /** Cancels an inquiry with a reason; its open tasks close and its alerts stop. Admins only. */
  async cancelInquiry(id: string, reason: string): Promise<Inquiry> {
    return this.request<Inquiry>(`/inquiries/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  }

  /** Adds a task to an inquiry. Staff only. */
  async createInquiryTask(
    inquiryId: string,
    data: CreateInquiryTaskRequest,
  ): Promise<WorkflowTask> {
    return this.request<WorkflowTask>(`/inquiries/${inquiryId}/tasks`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /** Changes one of an inquiry's tasks: its status, assignee and the rest. Staff only. */
  async updateInquiryTask(
    inquiryId: string,
    taskId: string,
    data: UpdateInquiryTaskRequest,
  ): Promise<WorkflowTask> {
    return this.request<WorkflowTask>(`/inquiries/${inquiryId}/tasks/${taskId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /** Reopens a cancelled inquiry, back to active work in the phase it was in. Admins only. */
  async reopenInquiry(id: string, note?: string | null): Promise<Inquiry> {
    return this.request<Inquiry>(`/inquiries/${id}/reopen`, {
      method: 'POST',
      body: JSON.stringify(note ? { note } : {}),
    })
  }

  async convertInquiry(
    id: string,
    data: ConvertInquiryRequest,
  ): Promise<{ inquiry: Inquiry; consignment: Consignment }> {
    return this.request<{ inquiry: Inquiry; consignment: Consignment }>(
      `/inquiries/${id}/convert`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )
  }

  async listCustomers(includeArchived = false): Promise<Customer[]> {
    return this.request<Customer[]>(
      `/customers?include_archived=${includeArchived ? 'true' : 'false'}`,
    )
  }

  async getCustomer(id: string): Promise<Customer> {
    return this.request<Customer>(`/customers/${id}`)
  }

  async createCustomer(data: CreateCustomerRequest): Promise<Customer> {
    return this.request<Customer>('/customers', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateCustomer(id: string, data: UpdateCustomerRequest): Promise<Customer> {
    return this.request<Customer>(`/customers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async getCustomerSignInEmail(id: string): Promise<CustomerSignInEmailStatus> {
    return this.request<CustomerSignInEmailStatus>(`/customers/${id}/sign-in-email`)
  }

  /** Emails a link to the new address; the customer's sign-in moves only once it is used. */
  async requestCustomerSignInEmailChange(
    id: string,
    newEmail: string,
  ): Promise<CustomerSignInEmailStatus> {
    return this.request<CustomerSignInEmailStatus>(`/customers/${id}/sign-in-email`, {
      method: 'POST',
      body: JSON.stringify({ new_email: newEmail }),
    })
  }

  async resendCustomerSignInEmailChange(id: string): Promise<CustomerSignInEmailStatus> {
    return this.request<CustomerSignInEmailStatus>(`/customers/${id}/sign-in-email/resend`, {
      method: 'POST',
    })
  }

  async cancelCustomerSignInEmailChange(id: string): Promise<CustomerSignInEmailStatus> {
    return this.request<CustomerSignInEmailStatus>(`/customers/${id}/sign-in-email`, {
      method: 'DELETE',
    })
  }

  async getSignInEmail(): Promise<SignInEmailStatus> {
    return this.request<SignInEmailStatus>('/account/sign-in-email')
  }

  /** Emails a link to the new address; your sign-in moves only once it is used. */
  async requestSignInEmailChange(newEmail: string): Promise<SignInEmailStatus> {
    return this.request<SignInEmailStatus>('/account/sign-in-email', {
      method: 'POST',
      body: JSON.stringify({ new_email: newEmail }),
    })
  }

  async resendSignInEmailChange(): Promise<SignInEmailStatus> {
    return this.request<SignInEmailStatus>('/account/sign-in-email/resend', { method: 'POST' })
  }

  async cancelSignInEmailChange(): Promise<SignInEmailStatus> {
    return this.request<SignInEmailStatus>('/account/sign-in-email', { method: 'DELETE' })
  }

  /** What a confirmation link would change, without using it. */
  async previewSignInEmailChange(token: string): Promise<SignInEmailPreview> {
    return this.request<SignInEmailPreview>(
      `/account/sign-in-email/confirm?token=${encodeURIComponent(token)}`,
    )
  }

  async confirmSignInEmailChange(token: string): Promise<{ email: string; notice_sent: boolean }> {
    return this.request<{ email: string; notice_sent: boolean }>('/account/sign-in-email/confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  }

  async archiveCustomer(id: string): Promise<Customer> {
    return this.request<Customer>(`/customers/${id}/archive`, {
      method: 'POST',
    })
  }

  async restoreCustomer(id: string): Promise<Customer> {
    return this.request<Customer>(`/customers/${id}/restore`, {
      method: 'POST',
    })
  }

  async deleteCustomer(id: string): Promise<{ deleted: true }> {
    return this.request<{ deleted: true }>(`/customers/${id}`, {
      method: 'DELETE',
    })
  }

  async listHelmUsers(): Promise<HelmUsersResponse> {
    return this.request<HelmUsersResponse>('/helm/users')
  }

  async inviteHelmUser(data: CreateHelmInvitationRequest): Promise<HelmStaffInvitation> {
    return this.request<HelmStaffInvitation>('/helm/invitations', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async acceptHelmInvitation(token: string): Promise<HelmStaffInvitation> {
    return this.request<HelmStaffInvitation>('/helm/invitations/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  }

  async resendHelmInvitation(id: string): Promise<HelmStaffInvitation> {
    return this.request<HelmStaffInvitation>(`/helm/invitations/${id}/resend`, { method: 'POST' })
  }

  /** Reads an invitation from its link; works before the invitee is signed in. */
  async previewHelmInvitation(token: string): Promise<HelmInvitationPreview> {
    return this.request<HelmInvitationPreview>(
      `/helm/invitations/preview?token=${encodeURIComponent(token)}`,
    )
  }

  /** Creates the invited person's account for the invited email, and accepts. */
  async registerFromHelmInvitation(input: {
    token: string
    name: string
    password: string
  }): Promise<{ email: string; role: HelmInvitationPreview['role'] }> {
    return this.request('/helm/invitations/register', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }

  async revokeHelmInvitation(id: string): Promise<HelmStaffInvitation> {
    return this.request<HelmStaffInvitation>(`/helm/invitations/${id}/revoke`, {
      method: 'POST',
    })
  }

  async updateHelmUserRoles(id: string, data: UpdateHelmUserRolesRequest): Promise<HelmUser> {
    return this.request<HelmUser>(`/helm/users/${id}/roles`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async listTrackingShares(consignmentId: string): Promise<TrackingShare[]> {
    return this.request<TrackingShare[]>(`/consignments/${consignmentId}/tracking-shares`)
  }

  async createTrackingShare(
    consignmentId: string,
    data: CreateTrackingShareRequest,
  ): Promise<TrackingShareReveal> {
    return this.request<TrackingShareReveal>(`/consignments/${consignmentId}/tracking-shares`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateTrackingShare(
    consignmentId: string,
    shareId: string,
    data: UpdateTrackingShareRequest,
  ): Promise<TrackingShare> {
    return this.request<TrackingShare>(
      `/consignments/${consignmentId}/tracking-shares/${shareId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    )
  }

  async rotateTrackingShare(consignmentId: string, shareId: string): Promise<TrackingShareReveal> {
    return this.request<TrackingShareReveal>(
      `/consignments/${consignmentId}/tracking-shares/${shareId}/rotate`,
      { method: 'POST' },
    )
  }

  async revokeTrackingShare(consignmentId: string, shareId: string): Promise<{ revoked: true }> {
    return this.request<{ revoked: true }>(
      `/consignments/${consignmentId}/tracking-shares/${shareId}`,
      { method: 'DELETE' },
    )
  }

  async exchangePublicTrackingToken(
    publicId: string,
    token: string,
  ): Promise<PublicTrackingExchangeResponse> {
    return this.request<PublicTrackingExchangeResponse>(`/public/tracking/${publicId}/exchange`, {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  }

  async unlockPublicTracking(publicId: string, passcode: string): Promise<{ unlocked: true }> {
    return this.request<{ unlocked: true }>(`/public/tracking/${publicId}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ passcode }),
    })
  }

  async getPublicTrackingSummary(): Promise<PublicTrackingSummary> {
    return this.request<PublicTrackingSummary>('/public/tracking/session/summary')
  }

  async getPublicTrackingTimeline(): Promise<PublicTrackingTimelineEvent[]> {
    return this.request<PublicTrackingTimelineEvent[]>('/public/tracking/session/timeline')
  }

  /** The download URL for a shared document. Bytes are proxied through the
   *  session, so the storage URL is never handed to the recipient. */
  publicTrackingDocumentUrl(documentId: string): string {
    return `${this.baseUrl}/public/tracking/session/documents/${encodeURIComponent(documentId)}/content`
  }

  async getPublicTrackingContainers(): Promise<PublicTrackingContainer[]> {
    return this.request<PublicTrackingContainer[]>('/public/tracking/session/containers')
  }

  async getPublicTrackingPosition(): Promise<PublicTrackingPosition | null> {
    return this.request<PublicTrackingPosition | null>('/public/tracking/session/position')
  }

  async getPublicTrackingDocument(documentId: string): Promise<PublicTrackingDocument> {
    return this.request<PublicTrackingDocument>(
      `/public/tracking/session/documents/${encodeURIComponent(documentId)}`,
    )
  }

  async closePublicTrackingSession(): Promise<{ signed_out: true }> {
    return this.request<{ signed_out: true }>('/public/tracking/session', { method: 'DELETE' })
  }

  /** Setup and source matching must include customers and historical records beyond page one. */
  private async allWhatsAppChoices<T extends { id: string }>(endpoint: string): Promise<T[]> {
    const items = new Map<string, T>()
    for (let page = 1; ; page++) {
      const result = await this.requestPage<T>(endpoint, { page: String(page), limit: '100' })
      for (const item of result.items) items.set(item.id, item)
      if (page >= result.totalPages) return [...items.values()]
      if (!result.items.length)
        throw new Error('The record list changed while loading. Refresh to load every choice.')
    }
  }

  async listWhatsAppCustomerChoices(): Promise<Customer[]> {
    return this.allWhatsAppChoices('/customers')
  }

  async listWhatsAppInquiryChoices(): Promise<Inquiry[]> {
    return this.allWhatsAppChoices('/inquiries')
  }

  async listWhatsAppConsignmentChoices(): Promise<Consignment[]> {
    return this.allWhatsAppChoices('/consignments')
  }

  async getWhatsAppStatus(): Promise<WhatsAppStatus> {
    return this.request('/integrations/whatsapp/status')
  }

  async startWhatsAppSession(input?: {
    recover_pairing?: boolean
  }): Promise<Pick<WhatsAppStatus, 'status' | 'account_present'>> {
    return this.request('/integrations/whatsapp/session/start', {
      method: 'POST',
      ...(input ? { body: JSON.stringify(input) } : {}),
    })
  }

  async getWhatsAppQr(): Promise<{ qr: string }> {
    return this.request('/integrations/whatsapp/session/qr')
  }

  async listWhatsAppGroups(): Promise<WhatsAppGroup[]> {
    return this.request('/integrations/whatsapp/groups')
  }

  async listWhatsAppBindings(): Promise<WhatsAppBinding[]> {
    return this.request('/integrations/whatsapp/bindings')
  }

  async bindWhatsAppGroup(input: {
    customer_id: string
    group_id: string
    participant_notice_confirmed: true
  }): Promise<WhatsAppBinding> {
    return this.request('/integrations/whatsapp/bindings', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }

  async updateWhatsAppBinding(id: string, active: boolean): Promise<WhatsAppBinding> {
    return this.request(`/integrations/whatsapp/bindings/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    })
  }

  async listWhatsAppMessages(
    bindingId: string,
    before?: string,
    messageId?: string,
  ): Promise<WhatsAppMessage[]> {
    const params = new URLSearchParams({ limit: '50' })
    if (before) params.set('before', before)
    if (messageId) params.set('message_id', messageId)
    return this.request(
      `/integrations/whatsapp/bindings/${encodeURIComponent(bindingId)}/messages?${params}`,
    )
  }

  async listWhatsAppOutbox(bindingId: string): Promise<WhatsAppOutbox[]> {
    return this.request(
      `/integrations/whatsapp/bindings/${encodeURIComponent(bindingId)}/outbox?limit=20`,
    )
  }

  async reconcileWhatsAppBinding(bindingId: string): Promise<{ queued: true }> {
    return this.request(
      `/integrations/whatsapp/bindings/${encodeURIComponent(bindingId)}/reconcile`,
      { method: 'POST' },
    )
  }

  async listWhatsAppBeaconProposals(bindingId: string): Promise<WhatsAppBeaconProposal[]> {
    return this.request(
      `/integrations/whatsapp/bindings/${encodeURIComponent(bindingId)}/beacon-proposals`,
    )
  }

  async decideWhatsAppBeaconProposal(
    proposalId: string,
    input: { decision: 'APPLY' | 'REJECT' | 'SEND'; fields: string[]; body?: string },
  ): Promise<WhatsAppBeaconProposal> {
    return this.request(
      `/integrations/whatsapp/beacon/proposals/${encodeURIComponent(proposalId)}/decision`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  }

  /** Groups whose Beacon proposals this person may review: an administrator's, or a record owner's. */
  async listWhatsAppReviewGroups(): Promise<WhatsAppReviewGroups> {
    return this.request('/integrations/whatsapp/beacon/review-groups')
  }

  /** Reads one active group's new messages now, through the hourly digest. Proposals still need review. */
  async requestWhatsAppBeaconDigest(
    bindingId: string,
    input: { idempotency_key: string },
  ): Promise<{ job_id?: string; status?: string; note?: string }> {
    return this.request(
      `/integrations/whatsapp/bindings/${encodeURIComponent(bindingId)}/beacon-digest`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  }

  /** Requests a draft only. Sending requires a separate administrator decision. */
  async requestWhatsAppBeaconUpdate(
    bindingId: string,
    input: { inquiry_id?: string; idempotency_key: string },
  ): Promise<{ job_id?: string; queued?: boolean; status?: string }> {
    return this.request(
      `/integrations/whatsapp/bindings/${encodeURIComponent(bindingId)}/beacon-update`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  }

  async queueWhatsAppReply(
    bindingId: string,
    input: {
      body: string
      idempotency_key: string
      reply_to?: string
    },
  ): Promise<WhatsAppOutbox> {
    return this.request(`/integrations/whatsapp/bindings/${encodeURIComponent(bindingId)}/send`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }

  async linkWhatsAppMessage(
    messageId: string,
    input: WhatsAppMessageLink,
  ): Promise<WhatsAppMessage> {
    return this.request(`/integrations/whatsapp/messages/${encodeURIComponent(messageId)}/links`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    })
  }

  async getOverview(): Promise<HelmOverview> {
    return this.request<HelmOverview>('/overview')
  }

  /** Everything the office wall and 3D terminal draw, in one read. Staff only. */
  async getDisplaySnapshot(): Promise<HelmDisplaySnapshot> {
    return this.request<HelmDisplaySnapshot>('/display/snapshot')
  }

  /** A signed link to an email attachment, valid for a few minutes. */
  async getAttachmentAccess(
    attachmentId: string,
    disposition: 'inline' | 'attachment' = 'inline',
  ): Promise<HelmAttachmentAccess> {
    return this.request<HelmAttachmentAccess>(
      `/communications/attachments/${attachmentId}/access?disposition=${disposition}`,
    )
  }

  async getInquiryDocumentAccess(
    inquiryId: string,
    documentId: string,
    disposition: 'inline' | 'attachment' = 'inline',
  ): Promise<HelmInquiryDocumentAccess> {
    return this.request<HelmInquiryDocumentAccess>(
      `/inquiries/${inquiryId}/documents/${documentId}/access?disposition=${disposition}`,
    )
  }

  async listReplyDrafts(inquiryId: string): Promise<HelmReplyDraft[]> {
    return this.request<HelmReplyDraft[]>(`/inquiries/${inquiryId}/reply-drafts`)
  }

  /** One draft in whatever state it is in: waiting, sent, discarded or replaced. */
  async getReplyDraft(id: string): Promise<HelmReplyDraft> {
    return this.request<HelmReplyDraft>(`/reply-drafts/${id}`)
  }

  /** Queues a draft; follow the returned job to know when it is ready. */
  async requestReplyDraft(inquiryId: string): Promise<{ job_id: string; status: string }> {
    return this.request(`/inquiries/${inquiryId}/reply-drafts`, { method: 'POST' })
  }

  async sendReplyDraft(
    id: string,
    edits: { subject?: string | null; body?: string; cc?: string[] },
  ): Promise<HelmCommunication> {
    return this.request<HelmCommunication>(`/reply-drafts/${id}/send`, {
      method: 'POST',
      body: JSON.stringify(edits),
    })
  }

  async discardReplyDraft(id: string): Promise<HelmReplyDraft> {
    return this.request<HelmReplyDraft>(`/reply-drafts/${id}/discard`, { method: 'POST' })
  }

  async getJob(id: string): Promise<HelmJobStatus> {
    return this.request<HelmJobStatus>(`/jobs/${id}`)
  }

  /** What the team owes clients now, most urgent first. Staff only. */
  async getAttention(owner?: 'me' | 'unassigned' | string): Promise<HelmAttention> {
    const query = owner ? `?owner=${encodeURIComponent(owner)}` : ''
    return this.request<HelmAttention>(`/attention${query}`)
  }

  /** Files a problem or idea with engineering (Linear), with page and browser context. */
  async reportProblem(input: FeedbackReportInput): Promise<FeedbackReportResult> {
    return this.request<FeedbackReportResult>('/feedback/reports', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }

  async listNotifications(
    options: { cursor?: string; limit?: number; includeResolved?: boolean } = {},
  ): Promise<HelmNotificationList> {
    const params = new URLSearchParams()
    if (options.cursor) params.set('cursor', options.cursor)
    if (options.limit) params.set('limit', String(options.limit))
    if (options.includeResolved !== undefined)
      params.set('include_resolved', String(options.includeResolved))
    return this.request<HelmNotificationList>(`/notifications${params.size ? `?${params}` : ''}`)
  }

  async markNotificationRead(id: string): Promise<HelmNotification> {
    return this.request<HelmNotification>(`/notifications/${id}/read`, { method: 'POST' })
  }

  async markAllNotificationsRead(): Promise<{ updated: number }> {
    return this.request<{ updated: number }>('/notifications/read-all', { method: 'POST' })
  }

  async getNotificationPreferences(): Promise<HelmNotificationPreferences> {
    return this.request<HelmNotificationPreferences>('/notifications/preferences')
  }

  async setNotificationPreferences(
    preferences: HelmNotificationPreferences,
  ): Promise<HelmNotificationPreferences> {
    return this.request<HelmNotificationPreferences>('/notifications/preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    })
  }

  /** Where the signed-in person is with Getting started and the guided tours. */
  async getOnboardingProgress(): Promise<HelmOnboardingProgress> {
    return this.request<HelmOnboardingProgress>('/onboarding/progress')
  }

  /** Merged into their progress on the server; returns the result. */
  async saveOnboardingProgress(
    update: HelmOnboardingProgressUpdate,
  ): Promise<HelmOnboardingProgress> {
    return this.request<HelmOnboardingProgress>('/onboarding/progress', {
      method: 'PUT',
      body: JSON.stringify(update),
    })
  }

  async getProfile(): Promise<Profile> {
    return this.request<Profile>('/profiles/me')
  }
}

// Export singleton instance
export const apiClient = new ApiClient()

// Export class for testing
export { ApiClient }
